import { diffBrandKitFields } from "@arrowsup/platform-dtos";
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  List,
  ListItemButton,
  ListItemText,
  Stack,
  ToggleButton,
  ToggleButtonGroup,
  Typography,
} from "@mui/material";
import { useMemo, useState } from "react";
import { useNotify } from "react-admin";
import {
  useBrandKitDraft,
  useBrandKitDraftMutations,
  useBrandKitVersion,
  useBrandKitVersions,
} from "../../logic/brand-kit/use-brand-kit";
import { BrandAssetsProvider } from "./brand-assets";
import { BrandKitDiffView } from "./brand-kit-diff-view";
import { BrandKitFieldsView } from "./brand-kit-fields-view";

const RestoreButton: React.FC<{
  versionNumber: number;
  onRestored: () => void;
}> = ({ versionNumber, onRestored }) => {
  const notify = useNotify();
  const { data: draft } = useBrandKitDraft();
  const { restore } = useBrandKitDraftMutations();

  const onClick = () => {
    if (
      !draft ||
      !window.confirm(
        `Replace the draft with version ${versionNumber}? Changes in the draft will be lost.`
      )
    ) {
      return;
    }

    restore.mutate(
      { versionNumber, ref: { id: draft.id, revision: draft.revision } },
      {
        onSuccess: () => {
          notify(`Version ${versionNumber} copied into the draft`);
          onRestored();
        },
        onError: () => notify("Restore failed", { type: "error" }),
      }
    );
  };

  return (
    <Button
      variant="outlined"
      disabled={!draft || restore.isPending}
      onClick={onClick}
    >
      Restore into draft
    </Button>
  );
};

enum View {
  Changes = "changes",
  Full = "full",
}

const VersionDetail: React.FC<{
  versionNumber: number;
  canUpdate: boolean;
  onRestored: () => void;
}> = ({ versionNumber, canUpdate, onRestored }) => {
  const [view, setView] = useState<View>(View.Changes);
  const { data: version, isLoading } = useBrandKitVersion(versionNumber);
  const previousNumber = versionNumber > 1 ? versionNumber - 1 : undefined;
  const previous = useBrandKitVersion(previousNumber);

  // Undefined until the previous version loads, so a failure isn't shown as
  // every field being added.
  const diff = useMemo(
    () =>
      version && (previousNumber === undefined || previous.data)
        ? diffBrandKitFields(previous.data?.fields ?? [], version.fields)
        : undefined,
    [previousNumber, previous.data, version]
  );

  const assets = useMemo(
    () => [...(version?.assets ?? []), ...(previous.data?.assets ?? [])],
    [version, previous.data]
  );

  if (isLoading || (previousNumber !== undefined && previous.isLoading)) {
    return <CircularProgress />;
  }

  if (!version) {
    return (
      <Alert severity="error">Failed to load version {versionNumber}</Alert>
    );
  }

  return (
    <Stack spacing={2}>
      <Stack direction="row" spacing={2} alignItems="center" flexWrap="wrap">
        <Typography variant="h6" flex={1}>
          Version {versionNumber}
        </Typography>
        <ToggleButtonGroup
          size="small"
          exclusive
          value={view}
          onChange={(_, v: View | null) => v && setView(v)}
        >
          <ToggleButton value={View.Changes}>Changes</ToggleButton>
          <ToggleButton value={View.Full}>Full kit</ToggleButton>
        </ToggleButtonGroup>
        {canUpdate && (
          <RestoreButton
            versionNumber={versionNumber}
            onRestored={onRestored}
          />
        )}
      </Stack>
      {version.changeNote && (
        <Typography sx={{ whiteSpace: "pre-wrap" }}>
          {version.changeNote}
        </Typography>
      )}
      <BrandAssetsProvider assets={assets}>
        {view === View.Changes && diff && <BrandKitDiffView diff={diff} />}
        {view === View.Changes && !diff && (
          <Alert severity="error">
            Failed to load version {previousNumber} to compare with
          </Alert>
        )}
        {view === View.Full && <BrandKitFieldsView fields={version.fields} />}
      </BrandAssetsProvider>
    </Stack>
  );
};

/** Published versions, with each one's changes from the version before. */
export const BrandKitHistory: React.FC<{
  canUpdate: boolean;
  onRestored: () => void;
}> = ({ canUpdate, onRestored }) => {
  const { data: versions, isLoading, isError } = useBrandKitVersions();
  const [selected, setSelected] = useState<number>();

  if (isLoading) {
    return <CircularProgress />;
  }

  if (isError || !versions) {
    return <Alert severity="error">Failed to load the version history</Alert>;
  }

  if (versions.length === 0) {
    return (
      <Typography color="text.secondary">
        No versions have been published yet.
      </Typography>
    );
  }

  const current = selected ?? versions[0].versionNumber;

  return (
    <Box display="flex" gap={3} flexWrap="wrap">
      <List dense sx={{ width: 280, flexShrink: 0 }}>
        {versions.map((v) => {
          const note = v.changeNote ? ` · ${v.changeNote}` : "";
          const by = v.updatedBy ? ` · ${v.updatedBy}` : "";
          const at = new Date(v.publishedAt).toLocaleString();

          return (
            <ListItemButton
              key={v.versionNumber}
              selected={v.versionNumber === current}
              onClick={() => setSelected(v.versionNumber)}
            >
              <ListItemText
                primary={`Version ${v.versionNumber}${note}`}
                primaryTypographyProps={{ noWrap: true }}
                secondary={`${at}${by}`}
              />
            </ListItemButton>
          );
        })}
      </List>
      <Box flex={1} minWidth={320}>
        <VersionDetail
          key={current}
          versionNumber={current}
          canUpdate={canUpdate}
          onRestored={onRestored}
        />
      </Box>
    </Box>
  );
};
